/**
 * This browser's exam-device id (3.1): a random id kept in local storage and
 * sent with every exam call as X-Exam-Device. An exam that binds an attempt to
 * the device that started it (additionalSettings.restrictResumes; backend
 * services/exam/accessRules.js) compares it with the one Start recorded, so a
 * second device cannot resume or write to the attempt.
 *
 * It says nothing about the person or the machine — it is random, and lives
 * only in this browser. Clearing the browser's storage makes a new one, which
 * the server then treats as another device: that is what the proctor's
 * "release device" is for.
 */
const KEY = 'univanta-exam-device';
const VALID = /^[A-Za-z0-9-]{8,64}$/;
let memo = null;

const fresh = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  const part = () => Math.random().toString(36).slice(2, 10).padEnd(8, '0');
  return `${part()}-${part()}-${part()}-${part()}`;
};

export function examDeviceId(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try {
    const kept = storage?.getItem(KEY);
    if (kept && VALID.test(kept)) return kept;
    const id = fresh();
    storage?.setItem(KEY, id);
    return id;
  } catch {
    // Storage refused (a locked-down browser): one id for this page's life.
    memo = memo || fresh();
    return memo;
  }
}

/**
 * How many touch points this screen has (navigator.maxTouchPoints), sent with
 * exam calls as X-Exam-Touch-Points. An iPad on iPadOS 13+ reports a Mac user
 * agent, so an exam closed to phones and tablets let it in; a Mac has 0 touch
 * points, an iPad 5. Nothing about the person.
 */
export function touchPoints(nav = typeof navigator !== 'undefined' ? navigator : null) {
  const n = Number(nav?.maxTouchPoints);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** The exam calls carry it: /exam/…, /exam-secondary-device/…, and the rest of the exam family. */
export const isExamCall = (url) => /^\/?exam[-/]/.test(String(url || ''));
